import { utils, write } from "xlsx";

/**
 * This function creates a workbook from the provided data and writes it to a specified format.
 * The workbook is created using the SheetJS and each key-value pair in the data object is treated as a sheet and its rows.
 * The output format and type of the workbook can be specified with the 'type' and 'bookType' parameters.
 *
 * @param {object} data - An object containing the rows for each sheet. The rows can also be a sheet object from SheetJS
 * @example
 * Array of rows example:
 * {
 *    sheet1: [
 *      {
 *          column1: 1,
 *          column2: "2",
 *          ...
 *      },
 *      ...
 *    ],
 *    sheet2: [
 *      {
 *          column1: "hello",
 *          column2: "no thanks",
 *          ...
 *      },
 *      ...
 *    ],
 *    ...
 * }
 *
 * Worksheet object example:
 * {
 *    sheet1: {
 *       A2: { t: 'd', v: '2023-05-21T04:42:59.000Z', z: 'm/d/yy' },
 *       B2: { t: 'n', v: 295.34487502275846 },
 *       C2: { t: 's', v: 'GRIDAVERAGE' },
 *       D2: { t: 'b', v: false },
 *       E2: { t: 's', v: 'Electricity Grid Average' },
 *       F2: { t: 's', v: 'test site0' },
 *       G2: { t: 's', v: 'Rental Car Bulk Upload.xls' },
 *       A1: { t: 's', v: 'timestamp' },
 *       B1: { t: 's', v: 'amount' },
 *       C1: { t: 's', v: 'dataTypeUUID' },
 *       E1: { t: 's', v: 'emissionType' },
 *       F1: { t: 's', v: 'siteName' },
 *       G1: { t: 's', v: 'fileName' },
 *       '!ref': 'A1:G2'
 *    },
 *    ...
 * }
 *
 * @param {object} [options={}] - The options for creating the workbook.
 *
 * @param {object} [options.columnTypes={}] - The data type for each column/header. For types, see https://docs.sheetjs.com/docs/csf/cell/
 * @example
 * {
 *    timestamp: "d",
 * }
 *
 * @param {string} [options.type=buffer] - Output type. For available values, see https://docs.sheetjs.com/docs/api/write-options/
 * @param {string} [options.bookType=xlsx] - Output format. For available values, see https://docs.sheetjs.com/docs/api/write-options/
 *
 * @returns {*} By default returns a Buffer. The return type is dependent on the 'type' parameter.
 */
export const createWorkbook = (data, { columnTypes = {}, type = "buffer", bookType = "xlsx" } = {}) => {
	const workbook = utils.book_new();

	for (const [sheetName, sheetData] of Object.entries(data)) {
		let sheet = {};

		if (isWorksheet(sheetData)) {
			sheet = sheetData;
		} else {
			const headers = Object.keys(sheetData[0]);
			sheet = utils.json_to_sheet(sheetData, {
				header: headers,
				cellDates: true,
			});

			// Specify the type for each column, if columnTypes is provided
			if (Object.keys(columnTypes).length > 0) {
				for (let columnIndex = 0; columnIndex < headers.length; columnIndex++) {
					const header = headers[columnIndex];
					const columnLetter = String.fromCharCode(65 + columnIndex); // 65 is the ASCII value for 'A'

					if (columnTypes[header]) {
						for (let rowIndex = 0; rowIndex < sheetData.length; rowIndex++) {
							const cellAddress = `${columnLetter}${rowIndex + 2}`; // +2 because headers are in row 1 and arrays are 0-indexed

							// Check if the cell exists and its value is truthy (not empty)
							if (sheet[cellAddress]?.v) {
								sheet[cellAddress].t = columnTypes[header];
							}
						}
					}
				}
			}
		}

		utils.book_append_sheet(workbook, sheet, sheetName);
	}

	return write(workbook, { type, bookType });
};

/**
 * Checks if a given object is a worksheet in SheetJS.
 *
 * @param {Object} obj - The object to check.
 * @returns {boolean} - Returns true if the object is a worksheet, false otherwise.
 */
export const isWorksheet = (obj) => {
	// Check if the object is not null/Array and is of type 'object'.
	if (!obj || Array.isArray(obj) || typeof obj !== "object") {
		return false;
	}

	// Check if the object has a '!ref' property of type 'string'.
	if (typeof obj["!ref"] !== "string") {
		return false;
	}

	// Check if the '!ref' property is in the correct format.
	const refFormat = /^([A-Z]+[0-9]+):([A-Z]+[0-9]+)$/;
	if (!refFormat.test(obj["!ref"])) {
		return false;
	}

	// Check if the keys of the object are A1-style addresses or special properties.
	const cellFormat = /^([A-Z]+[0-9]+)$/;
	for (const key in obj) {
		if (key.startsWith("!")) {
			continue;
		}

		if (
			!cellFormat.test(key) &&
			(typeof obj[key] !== "object" || obj[key] === null)
		) {
			return false;
		}
	}

	return true;
};
